/**
 * `<rt-banner timestamp="…">`: the dismissible announcement banner (old `PageBase.tsx`).
 * The server renders the (linkified) message with the `hidden` attribute; the element shows
 * it if the message is newer than the last dismissal stored in
 * `localStorage['banner-timestamp']` (same key and format as the old site: `Date.now()`
 * as a decimal string), so people who dismissed the current message don't see it again.
 * A descendant `[data-banner-dismiss]` dismisses it.
 */
import { Island, defineElement } from '../lib/dom';
import { readStorage, writeStorage } from '../lib/storage';

export const BANNER_STORAGE_KEY = 'banner-timestamp';

/** Accepts epoch milliseconds (`1730000000000`) or an ISO 8601 timestamp. */
export function parseBannerTimestamp(value: string | null): number | null {
  if (!value) return null;
  const trimmed = value.trim();
  const ms = /^\d+$/.test(trimmed) ? Number(trimmed) : Date.parse(trimmed);
  return Number.isFinite(ms) ? ms : null;
}

export function shouldShowBanner(
  messageTimestamp: number | null,
  storedDismissal: string | null,
): boolean {
  if (messageTimestamp === null) return false;
  const dismissedAt = Number(storedDismissal ?? '0');
  return messageTimestamp > (Number.isFinite(dismissedAt) ? dismissedAt : 0);
}

export class RtBanner extends Island {
  protected start(signal: AbortSignal): void {
    const ts = parseBannerTimestamp(this.getAttribute('timestamp'));
    this.hidden = !shouldShowBanner(ts, readStorage(BANNER_STORAGE_KEY));
    this.addEventListener(
      'click',
      (e) => {
        if (!(e.target as Element | null)?.closest('[data-banner-dismiss]')) return;
        e.preventDefault();
        this.hidden = true;
        writeStorage(BANNER_STORAGE_KEY, Date.now().toString());
      },
      { signal },
    );
  }
}

defineElement('rt-banner', RtBanner);

declare global {
  interface HTMLElementTagNameMap {
    'rt-banner': RtBanner;
  }
}
